import { useCallback, useState } from 'react'
import type { KeyboardShortcuts, KeyboardNavigationReturn, KeyboardKey } from './types'

/**
 * Core keyboard navigation hook for handling keyboard shortcuts and events
 * 
 * @param shortcuts - Object mapping keys to handler functions
 * @param options - Configuration options
 * @returns Object with keyboard event handlers and state
 * 
 * @example
 * ```tsx
 * const { handleKeyDown } = useKeyboardNavigation({
 *   Enter: () => console.log('Enter pressed'),
 *   Escape: () => console.log('Escape pressed')
 * })
 * 
 * return <input onKeyDown={handleKeyDown} />
 * ```
 */
export const useKeyboardNavigation = (
  shortcuts: KeyboardShortcuts = {},
  options: {
    preventDefault?: boolean
    stopPropagation?: boolean
    enabled?: boolean
  } = {}
): KeyboardNavigationReturn => {
  const [isHandling, setIsHandling] = useState(false)
  
  const {
    preventDefault = true,
    stopPropagation = false,
    enabled = true
  } = options

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (!enabled) return

    const key = event.key as KeyboardKey
    const handler = shortcuts[key]
    
    if (handler) {
      setIsHandling(true)
      
      if (preventDefault) {
        event.preventDefault()
      }
      
      if (stopPropagation) {
        event.stopPropagation()
      }
      
      // Execute handler and check if it returns false (to cancel default behavior)
      const result = handler(event.nativeEvent)
      
      // Allow handler to indicate whether default behavior should continue
      if (result === false && !preventDefault) {
        event.preventDefault()
      }
      
      // Reset handling state after a short delay
      setTimeout(() => setIsHandling(false), 100)
    }
  }, [shortcuts, preventDefault, stopPropagation, enabled])

  return {
    handleKeyDown,
    isHandling
  }
}

/**
 * Utility hook for common keyboard patterns
 * Provides pre-configured shortcuts for common use cases
 */
export const useCommonKeyboardPatterns = (handlers: {
  onEnter?: () => void
  onEscape?: () => void
  onTab?: () => void
  onShiftTab?: () => void
  onArrowUp?: () => void
  onArrowDown?: () => void
}) => {
  const shortcuts: KeyboardShortcuts = {}
  
  if (handlers.onEnter) shortcuts.Enter = handlers.onEnter
  if (handlers.onEscape) shortcuts.Escape = handlers.onEscape
  if (handlers.onTab) shortcuts.Tab = handlers.onTab
  if (handlers.onArrowUp) shortcuts.ArrowUp = handlers.onArrowUp
  if (handlers.onArrowDown) shortcuts.ArrowDown = handlers.onArrowDown
  
  return useKeyboardNavigation(shortcuts)
} 